// Per-requirement results as cards, problems first, each with the AI's
// evidence and timestamp chips that jump to that frame. Plus the outliers
// the model flagged outside the checklist.
import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { DIMENSIONS, RESULT, SEVERITY, TONE, orderResults } from './qcFormat';
import { fmtTs, toSeconds } from './qcTimeline';
import { asText } from './qcClient';
import { CriticalTag, DimensionIcon } from './aiUi';
import { C, Pill, SectionTitle } from './ui';

const STATUS_ICON = { met: CheckCircleRoundedIcon, missed: CancelRoundedIcon, unclear: HelpRoundedIcon };

// A timestamp; a button that shows that frame when it can be placed on the video.
export function TimeChip({ ts, onJump }) {
    const label = typeof ts === 'number' ? fmtTs(ts) : asText(ts);
    const t = toSeconds(ts);
    if (t == null || !onJump) return <span style={{ fontSize: 12, color: C.muted }}>{label}</span>;
    return (
        <button type="button" className="vqc-ts" onClick={() => onJump(t)} aria-label={`Show the frame at ${label}`} title="Show this frame"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 2, boxSizing: 'border-box', minHeight: 28, padding: '0 11px 0 6px', borderRadius: 999, border: `1px solid ${C.border}`, background: '#fff', color: C.navy700, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            <PlayArrowRoundedIcon aria-hidden="true" style={{ fontSize: 16 }} />{label}
        </button>
    );
}

function Evidence({ children }) {
    return (
        <div style={{ display: 'flex', gap: 8, marginTop: 9, padding: '8px 11px', background: '#f7f7fc', border: '1px solid #ecebf7', borderRadius: 9, fontSize: 13, color: C.body, lineHeight: 1.45 }}>
            <AutoAwesomeIcon aria-hidden="true" style={{ fontSize: 14, color: '#7c3aed', marginTop: 2, flexShrink: 0 }} />
            <span>{children}</span>
        </div>
    );
}

function RequirementCard({ result, onJump }) {
    const mark = RESULT[result.status] || RESULT.unclear;
    const tone = TONE[mark.tone];
    const Icon = STATUS_ICON[result.status] || HelpRoundedIcon;
    const dim = DIMENSIONS.find((d) => d.id === (result.dimension || 'visual'));
    const stamps = Array.isArray(result.timestamps) ? result.timestamps : [];
    const problem = result.status !== 'met';
    return (
        <li className="vqc-card" style={{
            display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 12,
            background: problem ? `linear-gradient(90deg, ${tone.soft} 0%, #ffffff 42%)` : '#fff',
            border: `1px solid ${problem ? `${tone.color}40` : C.line}`, borderLeft: `4px solid ${tone.color}`,
        }}>
            <Icon aria-hidden="true" style={{ fontSize: 22, color: tone.color, flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ flex: '1 1 260px', fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.4 }}>{asText(result.requirement)}</div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        {result.critical && <CriticalTag />}
                        <Pill bg={tone.soft} color={tone.ink}>{mark.label}</Pill>
                    </div>
                </div>
                {dim && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.muted, marginTop: 3 }}>
                        <DimensionIcon id={dim.id} size={14} />{dim.label}
                    </div>
                )}
                {asText(result.evidence) && <Evidence>{result.evidence}</Evidence>}
                {stamps.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {stamps.map((ts) => <TimeChip key={String(ts)} ts={ts} onJump={onJump} />)}
                    </div>
                )}
            </div>
        </li>
    );
}

export function ResultChecklist({ results, onJump }) {
    if (!results?.length) return null;
    return (
        <div>
            <SectionTitle hint="Each requirement from the brief, with what the AI reviewer saw. Problems first.">Checklist</SectionTitle>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {orderResults(results).map((r) => <RequirementCard key={r.item_id} result={r} onJump={onJump} />)}
            </ul>
        </div>
    );
}

export function Outliers({ outliers, onJump }) {
    const list = Array.isArray(outliers) ? outliers : [];
    if (!list.length) return null;
    return (
        <div>
            <SectionTitle hint="Things the AI reviewer noticed that are not on the checklist">Flagged</SectionTitle>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {list.map((o, i) => {
                    const severity = asText(o?.severity);
                    const s = SEVERITY[severity] || SEVERITY.low;
                    return (
                        // Outliers have no id; their order is fixed by the check.
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`${asText(o?.ts)}-${i}`} className="vqc-card" style={{
                            display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 12,
                            background: `linear-gradient(90deg, ${TONE.flag.soft} 0%, #ffffff 42%)`, border: `1px solid ${TONE.flag.color}33`, borderLeft: `4px solid ${TONE.flag.color}`,
                        }}>
                            <span aria-hidden="true" style={{ width: 11, height: 11, marginTop: 5, borderRadius: 2, transform: 'rotate(45deg)', background: TONE.flag.color, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, color: C.text, lineHeight: 1.45 }}>{asText(o?.note)}</div>
                                {o?.ts != null && <div style={{ marginTop: 8 }}><TimeChip ts={o.ts} onJump={onJump} /></div>}
                            </div>
                            {severity && <Pill bg={s.bg} color={s.color}>{severity} severity</Pill>}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
